/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { CompanyTree } from '@veeam-vspc/models/web-controllers';

import { addOriginToUrl, addParamsToQuery, deepCopy, HttpMethods, TransportRequestTypes } from '../../../../core';
import { clearBaseRequestFieldsFromData } from '../../../../helpers/request-helpers';

import type { TransportMiddlewareContext, TransportMiddlewareRequest, TransportMiddlewareResponse, NextFunction } from '../../../../core';

export const preparePortalFiltersToQuery = (
    filters: Record<string, string | CompanyTree>,
    isRest = false,
    manageLocationFilterManually = false,
): Record<string, string> => {
    const result: Record<string, string> = {};

    if (filters.locationFilter !== undefined && filters.locationFilter !== null && !manageLocationFilterManually) {
        result.locationFilter = filters.locationFilter as string;
    }

    if (
        isRest
        && filters.organizationFilter !== undefined
        && filters.organizationFilter !== null
        && (filters.organizationFilter as CompanyTree).instanceUid
    ) {
        result.organizationFilter = (filters.organizationFilter as CompanyTree).instanceUid;
    } else if (
        filters.organizationFilter !== undefined
        && filters.organizationFilter !== null
        && (filters.organizationFilter as CompanyTree).id?.toString() !== undefined
    ) {
        result.companyFilter = (filters.organizationFilter as CompanyTree).id?.toString();
    }

    if (filters.siteFilter !== undefined && filters.siteFilter !== null) {
        result.ccsFilter = filters.siteFilter as string;
    }

    return result;
};

export const processUrlWithFiltersMiddleware = <T, R, E>(
    next: NextFunction<TransportMiddlewareResponse<R, E> | Error>,
    ctx: TransportMiddlewareContext,
    req: TransportMiddlewareRequest<T>,
    res: TransportMiddlewareResponse<R, E>
) => {
    const { config, commands, middlewareConfigs } = ctx;
    const { originalUrl, originalData, options } = req;
    const { requestInit, type, skipBaseUrl } = options;
    const isRest = type === TransportRequestTypes.ToRestServer || type === TransportRequestTypes.ToStreamUploadFileRest;
    const isRestGetRequest = isRest && requestInit.method === HttpMethods.Get;
    const isRestPatchRequest = isRest && requestInit.method === HttpMethods.Patch;
    const baseUrlPrefix = skipBaseUrl ? '' : config.getBaseUrl();
    const urlPrefix = isRest ? config.getRestPrefix() : baseUrlPrefix;
    const { processUrlWithFiltersMiddleware } = middlewareConfigs;

    (
        (
            !isRestPatchRequest
            && !(originalData as any)?.disableGlobalFilters
            && !processUrlWithFiltersMiddleware?.ignoreGlobalFilters
        )
            ? commands.getPortalFilters<Record<string, string> | null>()
            : Promise.resolve(null)
    )
        .then((filters) => {
            if (filters) {
                return addParamsToQuery(originalUrl, preparePortalFiltersToQuery(filters, isRest, Boolean((originalData as any)?.locationFilter)));
            }

            return originalUrl;
        })
        .then((urlWithFilters) => {
            const preparedUrl: string = addOriginToUrl(`${urlPrefix}${urlWithFilters}`);

            if (originalData && originalData.hasOwnProperty('disableGlobalFilters')) {
                delete (originalData as any).disableGlobalFilters;
            }

            req.url = isRestGetRequest
                ? addParamsToQuery(preparedUrl, clearBaseRequestFieldsFromData(deepCopy(originalData) as Record<string, any>))
                : preparedUrl;

            next();
        });
};
